"use client";

import { useId, useState } from "react";
import {
  ArrowUpRight,
  Check,
  ChevronRight,
  Clock3,
  Coins,
  Database,
  LockKeyhole,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Workflow,
} from "lucide-react";
import { Badge, Button, Checkbox } from "../../design-system";
import {
  policyFlagLabels,
  policyGroups,
  policySettingDefinitions,
  policySettingsFixture,
  type PolicySettingRow,
  type PolicySettingsExample,
  type PolicySettingsView,
} from "./policy-settings-data";
import "./policy-settings.css";

export type {
  PolicySettingRow,
  PolicySettingsExample,
  PolicySettingsView,
} from "./policy-settings-data";
export { policySettingsFixture } from "./policy-settings-data";
export type PolicySettingsProps = {
  view?: PolicySettingsView;
  onSave?: (key: string, value: unknown) => Promise<void>;
  onOpenAiKeys?: () => void;
  canEdit?: boolean;
  example?: PolicySettingsExample;
};

const icons = {
  billing: Coins,
  sanctions: ShieldCheck,
  quota: Workflow,
  retention: Database,
  seo: Search,
  ai: Sparkles,
  other: Settings2,
};
const isNumberList = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every((item) => typeof item === "number");
const isFlags = (v: unknown): v is Record<string, boolean> =>
  !!v &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  Object.keys(v).length > 0 &&
  Object.values(v).every((item) => typeof item === "boolean");
const aiManaged = (key: string) =>
  key.startsWith("llm_") && key !== "llm_call_retention_days";
const equal = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);
const inputDraft = (value: unknown): unknown =>
  isNumberList(value)
    ? value.join(", ")
    : typeof value === "number"
      ? String(value)
      : (value ?? "");

function readable(value: unknown, key: string): string {
  const definition = policySettingDefinitions[key];
  if (typeof value === "boolean") return value ? "켜짐" : "꺼짐";
  if (value === "" || value === null || value === undefined)
    return key === "llm_relay_reasoning_effort" ? "모델 기본값" : "설정 안 함";
  if (isNumberList(value))
    return value.length
      ? `${value.join(", ")}${definition?.unit ? ` ${definition.unit}` : ""}`
      : "추가 수집 꺼짐";
  if (key === "llm_prices" || key === "llm_prices_meta")
    return `${Object.keys(value as object).length}개 모델`;
  if (isFlags(value))
    return `${Object.values(value).filter(Boolean).length}개 항목 켜짐`;
  if (typeof value === "object") return `${Object.keys(value).length}개 항목`;
  if (key === "llm_relay_reasoning_effort")
    return (
      (
        {
          minimal: "최소",
          low: "낮음",
          medium: "보통",
          high: "높음",
          xhigh: "매우 높음",
        } as Record<string, string>
      )[String(value)] ?? String(value)
    );
  return `${String(value)}${definition?.unit ? ` ${definition.unit}` : ""}`;
}

/** Values and defaults come from view; the parent refreshes its API query after onSave resolves. */
export function PolicySettings({
  view,
  onSave,
  onOpenAiKeys,
  canEdit = true,
  example = "normal",
}: PolicySettingsProps) {
  const [fixture] = useState(() => policySettingsFixture(example));
  const data = view ?? fixture;
  const editable =
    canEdit &&
    data.editable !== false &&
    example !== "readonly" &&
    (!view || !!onSave);
  const used = new Set<string>();
  const groups = policyGroups
    .map((group) => {
      const rows = data.settings.filter((row) => {
        if (used.has(row.key)) return false;
        if ("keys" in group)
          return (group.keys as readonly string[]).includes(row.key);
        if ("prefix" in group) return row.key.startsWith(group.prefix);
        return true;
      });
      rows.forEach((row) => used.add(row.key));
      return { ...group, rows };
    })
    .filter((group) => group.rows.length > 0);
  const save = async (key: string, value: unknown) => {
    if (onSave) return onSave(key, value);
    await new Promise<void>((resolve) => setTimeout(resolve, 350));
    if (example === "error")
      throw new Error(
        "정책을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요. (503)",
      );
  };
  return (
    <div className="mc-policy-settings">
      <div className="mc-policy-context">
        {editable ? (
          <Clock3 size={18} aria-hidden="true" />
        ) : (
          <LockKeyhole size={18} aria-hidden="true" />
        )}
        <div>
          <strong>
            {editable
              ? "변경한 항목만 저장합니다"
              : "운영 정책을 조회하고 있습니다"}
          </strong>
          <p>
            {editable
              ? "켜기·끄기와 체크 항목은 바로 저장됩니다. 저장한 정책은 30초 안에 반영됩니다."
              : "읽기 전용 권한입니다. 정책 변경은 요금 관리 권한이 있는 관리자에게 요청해 주세요."}
          </p>
        </div>
        <Badge tone={editable ? "blue" : "neutral"}>
          {editable ? "전체 고객사 적용" : "읽기 전용"}
        </Badge>
      </div>
      <nav className="mc-policy-jumps" aria-label="정책 묶음으로 이동">
        {groups.map((group) => (
          <a key={group.id} href={`#policy-${group.id}`}>
            {group.title}
            <ChevronRight size={12} aria-hidden="true" />
          </a>
        ))}
      </nav>
      {groups.map((group) => {
        const Icon = icons[group.id];
        return (
          <section
            className="mc-policy-panel"
            id={`policy-${group.id}`}
            aria-labelledby={`policy-${group.id}-title`}
            key={group.id}
          >
            <header className="mc-policy-panel-heading">
              <span className="mc-policy-group-icon">
                <Icon size={19} aria-hidden="true" />
              </span>
              <div>
                <h2 id={`policy-${group.id}-title`}>{group.title}</h2>
                <p>{group.description}</p>
              </div>
              {group.id === "ai" ? (
                onOpenAiKeys ? (
                  <Button variant="secondary" size="sm" onClick={onOpenAiKeys}>
                    AI 키 화면에서 관리
                    <ArrowUpRight size={14} />
                  </Button>
                ) : (
                  <a className="mc-policy-ai-link" href="/admin/ai-keys">
                    AI 키 화면에서 관리
                    <ArrowUpRight size={14} aria-hidden="true" />
                  </a>
                )
              ) : (
                <span className="mc-policy-group-count">
                  {group.rows.length}개 항목
                </span>
              )}
            </header>
            <div className="mc-policy-rows">
              {group.rows.map((row) => (
                <PolicySettingControl
                  key={row.key}
                  row={row}
                  editable={editable && !aiManaged(row.key)}
                  onSave={save}
                  errorExample={
                    example === "error" && row.key === "invoice_due_days"
                  }
                />
              ))}
            </div>
          </section>
        );
      })}
      {groups.length === 0 && (
        <div className="mc-policy-empty">
          <Settings2 size={24} aria-hidden="true" />
          <strong>표시할 운영 정책이 없습니다</strong>
          <p>정책을 불러오면 이곳에서 확인할 수 있습니다.</p>
        </div>
      )}
    </div>
  );
}

function PolicySettingControl({
  row,
  editable,
  onSave,
  errorExample,
}: {
  row: PolicySettingRow;
  editable: boolean;
  onSave: (key: string, value: unknown) => Promise<void>;
  errorExample: boolean;
}) {
  const id = useId();
  const definition = policySettingDefinitions[row.key];
  const label =
    definition?.label ?? row.description.split(" (")[0] ?? "운영 설정";
  const signature = JSON.stringify(row);
  const [state, setState] = useState(() => ({
    signature,
    saved: row,
    draft: errorExample ? ("120" as unknown) : inputDraft(row.value),
    dirty: errorExample,
    busy: false,
    error: errorExample ? "1~90일 사이의 정수를 입력해 주세요." : "",
    notice: "",
  }));
  // Fresh server data updates untouched fields without discarding an unsaved draft.
  if (signature !== state.signature && !state.busy) {
    setState({
      ...state,
      signature,
      saved: row,
      draft: state.dirty ? state.draft : inputDraft(row.value),
    });
  }
  const value = state.saved.value;
  const kind =
    typeof value === "boolean"
      ? "boolean"
      : typeof value === "number"
        ? "number"
        : isNumberList(value)
          ? "list"
          : isFlags(value) && !aiManaged(row.key)
            ? "flags"
            : value && typeof value === "object"
              ? "object"
              : "text";
  const canChange = editable && kind !== "object";
  const errorId = `${id}-error`;
  const helperId = `${id}-help`;
  const update = (draft: unknown) =>
    setState((current) => ({
      ...current,
      draft,
      dirty: !equal(draft, inputDraft(current.saved.value)),
      error: "",
      notice: "",
    }));
  const persist = async (next: unknown) => {
    setState((current) => ({
      ...current,
      draft: inputDraft(next),
      busy: true,
      dirty: true,
      error: "",
      notice: "",
    }));
    try {
      await onSave(row.key, next);
      setState((current) => ({
        ...current,
        saved: { ...current.saved, value: next, isDefault: false },
        draft: inputDraft(next),
        dirty: false,
        busy: false,
        notice: "저장됨",
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        busy: false,
        error: error instanceof Error ? error.message : String(error),
        notice: "",
      }));
    }
  };
  const submit = () => {
    const raw = String(state.draft).trim();
    let next: unknown = raw;
    let problem = "";
    if (kind === "number" || kind === "list") {
      const values =
        kind === "number"
          ? [Number(raw)]
          : raw === ""
            ? []
            : raw
                .split(",")
                .map((part) =>
                  part.trim() === "" ? NaN : Number(part.trim()),
                );
      if (
        (kind === "number" && raw === "") ||
        values.some(
          (number) => !Number.isFinite(number) || !Number.isInteger(number),
        )
      )
        problem =
          kind === "list"
            ? "정수를 쉼표로 구분해 입력해 주세요. 예: 1, 6, 24"
            : "정수를 입력해 주세요.";
      else if (definition?.maxItems && values.length > definition.maxItems)
        problem = `최대 ${definition.maxItems}개까지 입력할 수 있습니다.`;
      else if (
        values.some(
          (number) =>
            (definition?.min !== undefined && number < definition.min) ||
            (definition?.max !== undefined && number > definition.max),
        )
      )
        problem = `${definition?.min}~${definition?.max}${definition?.unit ?? ""} 사이의 정수를 입력해 주세요.`;
      next = kind === "number" ? values[0] : values;
    }
    if (problem) {
      setState((current) => ({ ...current, error: problem, notice: "" }));
      document.getElementById(id)?.focus();
      return;
    }
    void persist(next);
  };
  const defaultText = readable(state.saved.default, row.key);
  return (
    <div className={`mc-policy-row${state.error ? " has-error" : ""}`}>
      <div className="mc-policy-row-copy">
        <label
          htmlFor={canChange && kind !== "flags" ? id : undefined}
          id={`${id}-label`}
        >
          {label}
        </label>
        <p>{definition?.description ?? row.description}</p>
        <small className={state.saved.isDefault ? "is-default" : ""}>
          {state.saved.isDefault
            ? `기본값 사용 중 (${defaultText})`
            : `기본값: ${defaultText}`}
        </small>
      </div>
      <div className={`mc-policy-control mc-policy-control--${kind}`}>
        {!canChange ? (
          <div className="mc-policy-readonly">
            <span>{readable(state.saved.value, row.key)}</span>
            <small>
              <LockKeyhole size={12} aria-hidden="true" />
              {aiManaged(row.key) ? "AI 키 화면에서 관리" : "읽기 전용"}
            </small>
          </div>
        ) : kind === "boolean" ? (
          <div className="mc-policy-switch-line">
            <button
              id={id}
              className="mc-policy-switch"
              role="switch"
              aria-checked={Boolean(state.draft)}
              aria-labelledby={`${id}-label`}
              aria-describedby={state.error ? errorId : undefined}
              disabled={state.busy}
              onClick={() => void persist(!state.draft)}
            >
              <span />
            </button>
            <span>{state.draft ? "켜짐" : "꺼짐"}</span>
            {state.error && (
              <Button
                size="sm"
                variant="secondary"
                disabled={state.busy}
                onClick={() => void persist(state.draft)}
              >
                다시 저장
              </Button>
            )}
          </div>
        ) : kind === "flags" ? (
          <div
            className="mc-policy-flags"
            role="group"
            aria-labelledby={`${id}-label`}
            aria-describedby={state.error ? errorId : undefined}
          >
            {Object.entries(state.draft as Record<string, boolean>).map(
              ([key, checked], index) => (
                <Checkbox
                  key={key}
                  label={policyFlagLabels[key] ?? `산정 항목 ${index + 1}`}
                  checked={checked}
                  disabled={state.busy}
                  onChange={(next) =>
                    void persist({
                      ...(state.draft as Record<string, boolean>),
                      [key]: next,
                    })
                  }
                />
              ),
            )}
            {state.error && (
              <Button
                size="sm"
                variant="secondary"
                disabled={state.busy}
                onClick={() => void persist(state.draft)}
              >
                다시 저장
              </Button>
            )}
          </div>
        ) : (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <div className="mc-policy-input-line">
              <div className="mc-policy-input-wrap">
                <input
                  id={id}
                  className="input"
                  type={kind === "number" ? "number" : "text"}
                  inputMode={kind === "number" ? "numeric" : undefined}
                  step={kind === "number" ? 1 : undefined}
                  min={kind === "number" ? definition?.min : undefined}
                  max={kind === "number" ? definition?.max : undefined}
                  maxLength={definition?.maxLength}
                  value={String(state.draft)}
                  onChange={(event) => update(event.target.value)}
                  disabled={state.busy}
                  aria-invalid={!!state.error}
                  aria-describedby={`${helperId}${state.error ? ` ${errorId}` : ""}`}
                  placeholder={
                    kind === "list"
                      ? "예: 1, 6, 24"
                      : kind === "text"
                        ? "설정 안 함"
                        : undefined
                  }
                />
                {definition?.unit && <span>{definition.unit}</span>}
              </div>
              <Button
                type="submit"
                variant="secondary"
                size="sm"
                loading={state.busy}
                disabled={!state.dirty}
              >
                저장
              </Button>
            </div>
            <small id={helperId} className="mc-policy-hint">
              {kind === "list"
                ? `각 ${definition?.min}~${definition?.max}${definition?.unit ?? ""} · 최대 ${definition?.maxItems}개 · 쉼표로 구분`
                : kind === "number" && definition?.min !== undefined
                  ? `${definition.min}~${definition.max}${definition.unit ?? ""}`
                  : ""}
            </small>
          </form>
        )}
        {state.error && (
          <p id={errorId} className="mc-policy-error" role="alert">
            {state.error}
          </p>
        )}
        <span className="mc-policy-save-status" role="status">
          {state.busy ? (
            "저장 중…"
          ) : state.notice ? (
            <>
              <Check size={13} aria-hidden="true" />
              {state.notice}
            </>
          ) : state.dirty && !state.error ? (
            "저장하지 않은 변경"
          ) : (
            ""
          )}
        </span>
      </div>
    </div>
  );
}
